import { baseApi } from "@/shared/api";
import { authApi } from "@/features/auth/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { pendingActionsApi } from "./pendingActions";
import type { FSMSubmissionDetail, FSMPDFResponse } from "../types/fsm.types";

export const fsmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFSMSubmission: builder.query<FSMSubmissionDetail, string>({
      query: (id) => ({
        url: `/api/fsm/submissions/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "PendingActions" as const, id: `FSM-${id}` }],
    }),

    approveFSMSubmission: builder.mutation<FSMSubmissionDetail, string>({
      query: (id) => ({
        url: `/api/fsm/submissions/${id}/approve/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: ["PendingActions"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex((item) => item.id === id);
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              if (draft.summary.by_type.fsm_submission) {
                draft.summary.by_type.fsm_submission = Math.max(0, draft.summary.by_type.fsm_submission - 1);
              }
              if (draft.summary.by_action.review_fsm_submission) {
                draft.summary.by_action.review_fsm_submission = Math.max(0, draft.summary.by_action.review_fsm_submission - 1);
              }
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    rejectFSMSubmission: builder.mutation<FSMSubmissionDetail, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/api/fsm/submissions/${id}/reject/`,
        method: "POST",
        body: { reason },
        credentials: "include" as const,
      }),
      invalidatesTags: ["PendingActions"],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex((item) => item.id === id);
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              if (draft.summary.by_type.fsm_submission) {
                draft.summary.by_type.fsm_submission = Math.max(0, draft.summary.by_type.fsm_submission - 1);
              }
              if (draft.summary.by_action.review_fsm_submission) {
                draft.summary.by_action.review_fsm_submission = Math.max(0, draft.summary.by_action.review_fsm_submission - 1);
              }
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    getFSMPdf: builder.query<FSMPDFResponse, string>({
      query: (id) => ({
        url: `/api/fsm/submissions/${id}/pdf/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useGetFSMSubmissionQuery,
  useApproveFSMSubmissionMutation,
  useRejectFSMSubmissionMutation,
  useLazyGetFSMPdfQuery,
} = fsmApi;
